import { AfterContentInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ContentChildren,
  Directive, EventEmitter, Input, OnChanges, OnDestroy, Output, QueryList, TemplateRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { MonitorTab, MonitorTabId } from './device-monitor-tabs';

@Directive({ selector: 'ng-template[monitorTab]' })
export class MonitorTabDirective {
  @Input() monitorTab!: MonitorTabId;
  constructor(public readonly template: TemplateRef<unknown>) {}
}

/** Presentation only: the owner supplies available tabs and owns semantic selection. */
@Component({
  selector: 'app-monitor-tab-group',
  template: `
    <mat-tab-group animationDuration="0ms" [selectedIndex]="selectedIndex"
      (selectedTabChange)="onSelection($event.index)" [attr.aria-label]="ariaLabel">
      <mat-tab *ngFor="let tab of tabs; trackBy: trackTab" [label]="tab.label">
        <ng-template matTabContent>
          <ng-container *ngTemplateOutlet="templatesById[tab.id]"></ng-container>
        </ng-template>
      </mat-tab>
    </mat-tab-group>
  `,
  styles: [':host { display: block; min-width: 0; }'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MonitorTabGroupComponent implements OnChanges, AfterContentInit, OnDestroy {
  @Input() tabs: ReadonlyArray<MonitorTab> = [];
  @Input() activeId!: MonitorTabId;
  @Input() ariaLabel = 'Device monitor sections';
  @Output() tabSelected = new EventEmitter<MonitorTabId>();
  @ContentChildren(MonitorTabDirective) templates!: QueryList<MonitorTabDirective>;
  templatesById: { [id: string]: TemplateRef<unknown> } = {};
  selectedIndex = 0;
  private templatesSubscription!: Subscription;

  constructor(private readonly changeDetector: ChangeDetectorRef) {}

  ngOnChanges(): void {
    this.selectedIndex = Math.max(0, this.tabs.findIndex(tab => tab.id === this.activeId));
  }

  ngAfterContentInit(): void {
    this.indexTemplates();
    this.templatesSubscription = this.templates.changes.subscribe(() => this.indexTemplates());
  }

  private indexTemplates(): void {
    this.templatesById = {};
    this.templates.forEach(item => this.templatesById[item.monitorTab] = item.template);
    this.changeDetector.markForCheck();
  }

  onSelection(index: number): void {
    // Material also emits during initialization and programmatic selection.
    if (!this.tabs[index] || index === this.selectedIndex) return;
    this.selectedIndex = index;
    this.tabSelected.emit(this.tabs[index].id);
  }

  trackTab(_index: number, tab: MonitorTab): MonitorTabId { return tab.id; }

  ngOnDestroy(): void {
    if (this.templatesSubscription) this.templatesSubscription.unsubscribe();
  }
}
